<template>
    <div ref="container" class="model-preview">
        <canvas
            ref="canvas"
            role="img"
            :aria-label="component.description[0].name"
        />

        <model-preview-controls v-if="isKiosk" class="cz2__360-viewer__controls-kiosk" />
    </div>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .model-preview {
            position: relative;
            height: 400px;

            flex: 1 0 0;

            canvas {
                position: absolute;

                inset: 0;

                cursor: grab;
                &:active {
                    cursor: grabbing;
                }
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__model-preview__360 {
            margin: 0 auto;
            width: 100%;
        }

        .cz2__360-viewer__controls-kiosk {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            bottom: 0;
            margin: 0 auto;
            position: absolute;
            scale: 1.5;

            .cz2__model-preview__rotate {
                margin: 0 50px;
            }
        }
    }
</style>

<script>
    import axios from 'axios';

    import { Engine } from '@babylonjs/core/Engines/engine';
    import { Scene } from '@babylonjs/core/scene';
    import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
    import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
    import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline';
    // eslint-disable-next-line no-unused-vars
    import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
    import {
        EasingFunction,
        CubicEase,
    } from '@babylonjs/core/Animations/easing';
    import { Color4 } from '@babylonjs/core/Maths/math.color';
    import { Animation } from '@babylonjs/core/Animations/animation';

    import ResizeObserver from 'resize-observer-polyfill';

    import ModelPreviewControls from './ModelPreviewControls.vue';

    export default {
        components: {
            ModelPreviewControls,
        },

        props: {
            component: Object,
        },

        computed: {
            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            viewController() {
                return this.$store.state.customizer.viewController;
            },

            previewRotation() {
                return this.$store.state.customizer.previewRotation;
            },
        },

        mounted() {
            setTimeout(() => {
                this.alive = true;

                this.resizeObserver = new ResizeObserver(() => {
                    this.resize();
                });

                this.resizeObserver.observe(this.$refs.container);

                this.resolvePrimaryUrl();
            }, 300);

            this.emitter.$on('previewRotation', this.rotatePreview);
        },

        beforeDestroy() {
            this.alive = false;

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }

            this.unload();

            this.emitter.$off('previewRotation');
        },

        methods: {
            resize() {
                if (this.$refs.canvas && this.$refs.container) {
                    const w = this.$refs.container.offsetWidth;
                    const h = this.$refs.container.offsetHeight;

                    this.$refs.canvas.style.width = `${w}px`;
                    this.$refs.canvas.style.height = `${h}px`;

                    this.$refs.canvas.width = w;
                    this.$refs.canvas.height = h;

                    if (this.engine) {
                        this.engine.resize();
                    }
                }
            },

            unload() {
                if (this.engine) {
                    this.engine.dispose();
                    this.engine = null;
                }
            },

            load() {
                this.unload();

                if (this.primaryUrl == null) {
                    return;
                }

                if (!this.alive) {
                    return;
                }

                this.resize();

                this.engine = new Engine(this.$refs.canvas, true, {
                    adaptToDeviceRatio: true,
                    limitDeviceRatio: 2,
                    preserveDrawingBuffer: true,
                }, true);

                // Make a custom loading screen.
                // eslint-disable-next-line no-inner-declarations
                function CustomLoadingScreen() {
                }

                CustomLoadingScreen.prototype.displayLoadingUI = () => {
                    this.loadingMain = 'loading';

                    // eslint-disable-next-line
                    console.log('Show loading screen');
                };

                CustomLoadingScreen.prototype.hideLoadingUI = () => {
                    this.loadingMain = 'done';

                    // eslint-disable-next-line
                    console.log('Hide loading screen');
                };

                this.engine.loadingScreen = new CustomLoadingScreen();

                this.engine.canvasTabIndex = -1;

                this.scene = new Scene(this.engine);

                this.scene.clearColor = new Color4(0, 0, 0, 0);

                const envMap = CubeTexture.CreateFromPrefilteredData(this.getEnvironmentUrl(), this.scene);

                this.scene.environmentTexture = envMap;

                const baseIndex = this.primaryUrl.lastIndexOf('/');

                const baseUrl = this.primaryUrl.substr(0, baseIndex + 1);
                const name = this.primaryUrl.substr(baseIndex + 1);

                SceneLoader.Append(baseUrl, name, this.scene, (scene) => {
                    scene.createDefaultCameraOrLight(true, true, true);

                    this.scene.activeCamera.panningSensibility = 0;
                    this.scene.activeCamera.lowerRadiusLimit = this.scene.activeCamera.radius;
                    this.scene.activeCamera.upperRadiusLimit = this.scene.activeCamera.radius;

                    this.pipeline = new DefaultRenderingPipeline(
                        'main-pipeline',
                        true,
                        this.scene,
                        this.scene.cameras,
                    );

                    this.pipeline.samples = 4;
                    this.pipeline.fxaaEnabled = true;

                    this.engine.runRenderLoop(() => {
                        if (this.scene) {
                            this.scene.render();
                        }
                    });
                });
            },

            getEnvironmentUrl() {
                // Figure out the current environment.
                const selectedEnvironment = this.viewController.selectedAtPlacement('POPEnvironment');

                // Find the first camera view.
                const view = this.viewController.blueprint.views.find((x) => x.viewType === 'Camera');

                const name = this.viewController.imageName(selectedEnvironment, null, view, view.frameLow);

                return `${this.viewController.baseFrameUrl(true)}${name}.env`;
            },

            resolvePrimaryUrl() {
                if (this.component) {
                    // Find the first camera view.
                    const view = this.viewController.blueprint.views.find((x) => x.viewType === 'Camera');

                    let name = this.viewController.imageName(this.component, null, view, view.frameLow);

                    if (this.component.custom && this.component.custom['three-substitute-with']) {
                        name = this.component.custom['three-substitute-with'];
                    }

                    const sniffUrl = `${this.viewController.baseFrameUrl(true)}${name}.gltf`;

                    axios.head(sniffUrl).then((sniffResponse) => {
                        const contentType = sniffResponse.headers['content-type'];

                        let fileName = sniffUrl;
                        if (contentType && contentType.includes('binary')) {
                            fileName = sniffUrl.replace('.gltf', '.glb');
                        }

                        this.primaryUrl = fileName;

                        this.$nextTick(() => {
                            this.load();
                        });
                    });
                }
            },

            rotatePreview() {
                if (this.scene?.activeCamera) {
                    const ease = new CubicEase();
                    ease.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);

                    const rads = (this.previewRotation * Math.PI) / 180;

                    Animation.CreateAndStartAnimation(
                        `rotate-${(new Date()).getTime()}`,
                        this.scene.activeCamera,
                        'alpha',
                        30, // FPS
                        10, // Duration (in frames)
                        this.scene.activeCamera.alpha,
                        this.scene.activeCamera.alpha + rads,
                        Animation.ANIMATIONLOOPMODE_CONSTANT,
                        ease,
                        () => {
                            // Nothing to do really when the animation finishes.
                        },
                    );

                    // Reset the rotation angle since it got applied to the camera.
                    this.$store.dispatch('setPreviewRotation', 0);
                }
            },
        },
    };
</script>
